import React, { useState, useRef, useEffect } from "react";
import { HTML5Backend } from "react-dnd-html5-backend";
import { DndProvider } from "react-dnd/dist/core";
import { useDrag, useDrop } from "react-dnd/dist/hooks";
import API from "../api/axiosInstance";
import {
    Box,
    Breadcrumbs,
    Button,
    Typography,
    Select,
    MenuItem,
    TextField,
    FormControl,
    InputLabel,
    Stack,
    IconButton
} from "@mui/material";
import { AutomationEditorProps, AutomationStep } from "../types/interfaces";
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import "../styles/AutomationEditor.css";
import { createUUID } from "../utils/validators";

const ItemTypes = { STEP: "STEP" };

const AutomationEditor: React.FC<AutomationEditorProps> = ({ automation, onSave, onClose }) => {
    const [steps, setSteps] = useState<AutomationStep[]>(automation.steps || []);
    const [breadcrumb, setBreadcrumb] = useState<string[]>([automation.name]);
    const [isCheckedOut, setIsCheckedOut] = useState<boolean>(false);
    const [isTesting, setIsTesting] = useState<boolean>(false);
    const [testOutput, setTestOutput] = useState<string | null>(null);

    const moveStep = (from: number, to: number) => {
        const reordered = [...steps];
        const [moved] = reordered.splice(from, 1);
        reordered.splice(to, 0, moved);
        setSteps(reordered);
    };

    const updateStep = (index: number, update: Partial<AutomationStep>) => {
        const updated = [...steps];
        updated[index] = { ...updated[index], ...update };
        setSteps(updated);
    };

    const removeStep = (index: number) => {
        setSteps((prev) => prev.filter((_, i) => i !== index));
    };

    const handleCheckout = async () => {
        try {
            await API.post(`/tables/automations/${automation.automationID}/checkout`);
            setIsCheckedOut(true);
        } catch (err) {
            console.error("Checkout failed", err);
        }
    };

    const handleCheckin = async () => {
        try {
            await API.post(`/tables/automations/${automation.automationID}/checkin`);
            setIsCheckedOut(false);
        } catch (err) {
            console.error("Check-in failed", err);
        }
    };

    const handleSave = async () => {
        try {
            await API.put(`/tables/automations/${automation.automationID}`, {
                ...automation,
                steps
            });
            onSave();
        } catch (err) {
            console.error("Save failed", err);
        }
    };

    const handleTest = async () => {
        setIsTesting(true);
        try {
            const res = await API.post(`/tables/automations/${automation.automationID}/test`, {
                steps,
                testMode: true
            });
            setTestOutput(JSON.stringify(res.data, null, 2));
        } catch (err) {
            console.error("Test failed", err);
            setTestOutput("Error running test.");
        }
        setIsTesting(false);
    };

    const handleAddStep = () => {
        setSteps((prev) => [
            ...prev,
            {
                automationID: automation.automationID ,
                stepID: createUUID(),
                stepType: "ACTION",
                configJSON: "{}"
            }
        ]);
    };

    return (
        <DndProvider backend={HTML5Backend}>
            <Box className="automation-editor" sx={{ p: 2 }}>
                <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
                    {breadcrumb.map((crumb, i) => (
                        <Typography key={i} color="text.primary">
                            {crumb}
                        </Typography>
                    ))}
                </Breadcrumbs>

                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                    <TextField
                        label="Automation Name"
                        value={automation.name}
                        onChange={(e) => (automation.name = e.target.value)}
                        fullWidth
                    />
                    <FormControl fullWidth>
                        <InputLabel>Trigger Event</InputLabel>
                        <Select
                            value={automation.triggerEvent ?? "INSERT"}
                            onChange={(e) => {
                                const value = e.target.value as "INSERT" | "UPDATE" | "DELETE" | "MANUAL";
                                automation.triggerEvent = value;
                            }}
                            label="Trigger Event"
                        >
                            <MenuItem value="INSERT">Row Added</MenuItem>
                            <MenuItem value="UPDATE">Row Updated</MenuItem>
                            <MenuItem value="DELETE">Row Deleted</MenuItem>
                        </Select>
                    </FormControl>
                </Stack>

                <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                    <Button variant="contained" onClick={handleSave} disabled={!isCheckedOut}>Save</Button>
                    <Button variant="outlined" onClick={handleCheckout} disabled={isCheckedOut}>Check Out</Button>
                    <Button variant="outlined" onClick={handleCheckin} disabled={!isCheckedOut}>Check In</Button>
                    <Button variant="contained" color="secondary" onClick={handleTest} disabled={isTesting}>
                        {isTesting ? "Testing..." : "Test Workflow"}
                    </Button>
                    <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddStep}>
                        Add Step
                    </Button>
                </Box>

                <Box className="step-list">
                    {steps.map((step, index) => (
                        <DraggableStep
                            key={step.stepID}
                            index={index}
                            step={step}
                            moveStep={moveStep}
                            updateStep={updateStep}
                            onRemove={() => removeStep(index)}
                        />
                    ))}
                </Box>

                {testOutput && (
                    <Box sx={{ mt: 3 }}>
                        <Typography variant="h6">Test Output</Typography>
                        <pre
                            style={{
                                whiteSpace: "pre-wrap",
                                backgroundColor: "#f5f5f5",
                                padding: "12px",
                                borderRadius: "4px",
                                maxHeight: 300,
                                overflow: "auto"
                            }}
                        >
                            {testOutput}
                        </pre>
                    </Box>
                )}
            </Box>
        </DndProvider>
    );
};

interface DraggableStepProps {
    index: number;
    step: AutomationStep;
    moveStep: (from: number, to: number) => void;
    updateStep: (index: number, update: Partial<AutomationStep>) => void;
    onRemove: () => void;
}

const DraggableStep: React.FC<DraggableStepProps> = ({
    index,
    step,
    moveStep,
    updateStep,
    onRemove
}) => {
    const ref = useRef<HTMLDivElement>(null);

    const [, drag] = useDrag({
        type: ItemTypes.STEP,
        item: { index }
    });

    const [, drop] = useDrop({
        accept: ItemTypes.STEP,
        hover: (item: { index: number }) => {
            if (item.index !== index) {
                moveStep(item.index, index);
                item.index = index;
            }
        }
    });

    drag(drop(ref));

    return (
        <Box ref={ref} className="step-card" sx={{
            border: "1px solid #ccc",
            borderRadius: 2,
            p: 2,
            mb: 1,
            backgroundColor: "#fff"
        }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle1">Step {index + 1}</Typography>
                <IconButton size="small" onClick={onRemove}>
                    <DeleteIcon fontSize="small" />
                </IconButton>
            </Stack>

            <FormControl fullWidth sx={{ mb: 1 }}>
                <InputLabel>Step Type</InputLabel>
                <Select
                    value={step.stepType}
                    label="Step Type"
                    onChange={(e) =>
                        updateStep(index, { stepType: e.target.value as AutomationStep["stepType"] })
                    }
                >
                    <MenuItem value="IF">IF Condition</MenuItem>
                    <MenuItem value="ACTION">Perform Action</MenuItem>
                    <MenuItem value="EMAIL">Send Email</MenuItem>
                    <MenuItem value="LOOP">Loop Records</MenuItem>
                    <MenuItem value="WEBAPI">Web API Call</MenuItem>
                    <MenuItem value="EXPORT">Export File</MenuItem>
                </Select>
            </FormControl>

            <TextField
                label="Step Configuration (JSON)"
                multiline
                rows={3}
                value={step.configJSON}
                onChange={(e) => updateStep(index, { configJSON: e.target.value })}
                fullWidth
                variant="outlined"
            />
        </Box>
    );
};

export default AutomationEditor;
